/*
 * /pages/stolen-bike-check — frame-number lookup behaviour (DEV-191).
 *
 * Presentation only: the lookup is answered by the mnb-edge stolen-bike-check
 * worker at the API base configured on the section (`data-api-base`), which
 * reads Bike Index. Nothing is stored here and nothing is written anywhere.
 *
 * Every provider value is rendered with textContent / setAttribute — never as
 * HTML — and links are only ever made to Bike Index's own https pages.
 *
 * The module is exposed on `window.StolenBikeCheck` so the repo's jsdom tests
 * drive the shipped code rather than a re-implementation of it.
 */
(function () {
  "use strict";

  var MIN_LENGTH = 4;
  var MAX_LENGTH = 32;
  var MAX_RAW_LENGTH = 64;
  var CLIENT_TIMEOUT_MS = 10000;
  var ALLOWED_RAW = /^[A-Za-z0-9 .\/-]+$/;
  var PLACEHOLDERS = ["UNKNOWN", "NONE", "NOSERIAL", "NOTKNOWN", "NA"];

  var MESSAGES = {
    missing: "Enter the bike's frame or serial number.",
    invalid_characters: "Use only letters, numbers, spaces, hyphens, dots or slashes.",
    too_short: "A frame number has at least " + MIN_LENGTH + " letters or numbers.",
    too_long: "A frame number has at most " + MAX_LENGTH + " letters or numbers.",
    placeholder: "Enter the number stamped on the frame, not a placeholder like \"unknown\".",
  };

  var OUTCOMES = {
    exact: {
      heading: "Reported stolen \u2014 exact match",
      body: [
        "Bike Index lists a stolen bike with this exact frame number. Do not buy it or hand over money.",
        "Check the number on the bike itself. If it matches, contact the police and give them the Bike Index link below.",
      ],
    },
    possible: {
      heading: "Possible match",
      body: [
        "Bike Index lists stolen bikes with a similar frame number. That can be a typo, a shared prefix or the same bike.",
        "Compare the make, model, colour and the number stamped on the frame with each record before going any further.",
      ],
    },
    none: {
      heading: "No stolen report found",
      body: [
        "Bike Index has no stolen report for this exact frame number.",
        "This is not proof that the seller owns the bike: not every theft is reported, and Bike Index is one registry. Keep following the checks below.",
      ],
    },
    unable: {
      heading: "Unable to check",
      body: [
        "We could not get an answer from the stolen-bike registry, so we cannot say whether this bike has been reported stolen.",
        "Please try again in a few minutes. Until then, treat the bike as unchecked.",
      ],
    },
  };

  var RATE_LIMITED_NOTE = "You have made a lot of checks in a short time. Wait a minute before trying again.";
  var INCOMPLETE_NOTE = "Part of this check could not be completed, so there may be other matching reports.";
  var MORE_AT_PROVIDER_NOTE = "Bike Index holds more records for this number than we can list here \u2014 ";

  function normalizeSerial(raw) {
    return String(raw == null ? "" : raw).toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function validateSerial(raw) {
    var trimmed = String(raw == null ? "" : raw).trim();
    if (!trimmed) return { ok: false, error: "missing" };
    if (trimmed.length > MAX_RAW_LENGTH) return { ok: false, error: "too_long" };
    if (!ALLOWED_RAW.test(trimmed)) return { ok: false, error: "invalid_characters" };
    var serial = normalizeSerial(trimmed);
    if (PLACEHOLDERS.indexOf(serial) !== -1) return { ok: false, error: "placeholder" };
    if (serial.length < MIN_LENGTH) return { ok: false, error: "too_short" };
    if (serial.length > MAX_LENGTH) return { ok: false, error: "too_long" };
    return { ok: true, serial: serial };
  }

  function safeUrl(value, imageHost) {
    if (typeof value !== "string") return null;
    try {
      var parsed = new URL(value);
      if (parsed.protocol !== "https:") return null;
      var host = parsed.hostname;
      if (host === "bikeindex.org") return parsed.toString();
      if (imageHost && /\.bikeindex\.org$/.test(host)) return parsed.toString();
      return null;
    } catch (_) {
      return null;
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function link(href, text) {
    var a = el("a", null, text);
    a.setAttribute("href", href);
    a.setAttribute("rel", "noopener noreferrer");
    a.setAttribute("target", "_blank");
    return a;
  }

  function formatDate(iso) {
    if (typeof iso !== "string") return null;
    var date = new Date(iso);
    if (isNaN(date.getTime())) return null;
    try {
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
    } catch (_) {
      return iso.slice(0, 10);
    }
  }

  function text(value) {
    return typeof value === "string" && value.trim() ? value.trim() : null;
  }

  function matchItem(bike) {
    var item = el("li", "sbc__match");
    var thumb = safeUrl(bike.thumb, true);
    var title = text(bike.title) || [text(bike.manufacturer), text(bike.model)].filter(Boolean).join(" ") || "Bike record";
    if (thumb) {
      item.className += " sbc__match--with-image";
      var img = el("img");
      img.setAttribute("src", thumb);
      img.setAttribute("alt", title);
      img.setAttribute("loading", "lazy");
      img.setAttribute("width", "96");
      img.setAttribute("height", "96");
      item.appendChild(img);
    }
    var body = el("div");
    var exact = bike.match === "exact";
    body.appendChild(el("span", "sbc__tag" + (exact ? " sbc__tag--exact" : ""), exact ? "Exact serial" : "Similar serial"));
    body.appendChild(el("h3", null, title));

    var record = [];
    if (text(bike.serial)) record.push("Serial " + text(bike.serial));
    if (typeof bike.year === "number") record.push(String(bike.year));
    if (Array.isArray(bike.colours) && bike.colours.length) record.push(bike.colours.filter(text).join(" / "));
    if (record.length) body.appendChild(el("p", "sbc__record", record.join(" \u00b7 ")));

    var stolen = [];
    var when = formatDate(bike.stolenAt);
    if (when) stolen.push("Reported stolen " + when);
    if (text(bike.stolenLocation)) stolen.push(text(bike.stolenLocation));
    if (stolen.length) body.appendChild(el("p", null, stolen.join(" \u00b7 ")));

    var href = safeUrl(bike.url, false);
    if (href) {
      var p = el("p");
      p.appendChild(link(href, "View this report on Bike Index"));
      body.appendChild(p);
    }
    item.appendChild(body);
    return item;
  }

  /**
   * Render one outcome into `container`. `data` is the worker's JSON (or null),
   * `options.rateLimited` adds the slow-down note to an `unable` result.
   * Any outcome value other than exact / possible / none renders as unable.
   */
  function renderResult(container, data, options) {
    options = options || {};
    var outcome = data && Object.prototype.hasOwnProperty.call(OUTCOMES, data.outcome) ? data.outcome : "unable";
    var copy = OUTCOMES[outcome];
    container.textContent = "";

    var verdict = el("div", "sbc__verdict sbc__verdict--" + outcome);
    verdict.setAttribute("data-outcome", outcome);
    var heading = el("h2", null, copy.heading);
    heading.id = "sbc-result-heading";
    heading.setAttribute("tabindex", "-1");
    verdict.appendChild(heading);
    copy.body.forEach(function (line) {
      verdict.appendChild(el("p", null, line));
    });
    if (outcome === "unable" && options.rateLimited) verdict.appendChild(el("p", null, RATE_LIMITED_NOTE));
    if (outcome !== "unable" && data && typeof data.serial === "string") {
      verdict.appendChild(el("p", "sbc__record", "Checked " + data.serial));
    }
    container.appendChild(verdict);

    if (outcome === "exact" || outcome === "possible") {
      var matches = Array.isArray(data.matches) ? data.matches.filter(function (m) { return m && typeof m === "object"; }) : [];
      if (data.complete === false) container.appendChild(el("p", "sbc__note", INCOMPLETE_NOTE));
      if (matches.length) {
        var list = el("ul", "sbc__matches");
        list.setAttribute("aria-label", "Matching stolen-bike records");
        matches.forEach(function (bike) {
          list.appendChild(matchItem(bike));
        });
        container.appendChild(list);
      }
      if (data.partial === true) {
        container.appendChild(el("p", "sbc__note", "Showing " + matches.length + " of the records we found."));
      }
      if (data.moreAtProvider === true && typeof data.serial === "string") {
        var more = el("p", "sbc__note", MORE_AT_PROVIDER_NOTE);
        more.appendChild(
          link(
            "https://bikeindex.org/bikes?serial=" + encodeURIComponent(data.serial) + "&stolenness=stolen",
            "see every record on Bike Index",
          ),
        );
        more.appendChild(document.createTextNode("."));
        container.appendChild(more);
      }
    }

    container.hidden = false;
    return { outcome: outcome, heading: heading };
  }

  function apiUrl(base, serial) {
    var clean = String(base || "").trim().replace(/\/+$/, "");
    if (!/^https:\/\//.test(clean)) return null;
    return clean + "/check?serial=" + encodeURIComponent(serial);
  }

  /** Ask the worker; resolve to { data, rateLimited, invalid } — never rejects. */
  function lookup(base, serial, fetchImpl) {
    var url = apiUrl(base, serial);
    var doFetch = fetchImpl || (typeof fetch === "function" ? fetch.bind(window) : null);
    if (!url || !doFetch) return Promise.resolve({ data: null });

    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, CLIENT_TIMEOUT_MS) : null;
    var init = { method: "GET", headers: { Accept: "application/json" } };
    if (controller) init.signal = controller.signal;

    return Promise.resolve()
      .then(function () { return doFetch(url, init); })
      .then(function (res) {
        if (res.status === 429) return { data: null, rateLimited: true };
        return res.json().then(
          function (body) {
            if (res.status === 400 && body && body.outcome === "invalid") return { data: null, invalid: body };
            if (!res.ok) return { data: null };
            return { data: body };
          },
          function () { return { data: null }; },
        );
      })
      .catch(function () { return { data: null }; })
      .then(function (result) {
        if (timer) clearTimeout(timer);
        return result;
      });
  }

  function init(root, deps) {
    deps = deps || {};
    if (!root || root.__sbcReady) return;
    root.__sbcReady = true;
    var form = root.querySelector("[data-sbc-form]");
    var input = root.querySelector("#sbc-serial");
    var error = root.querySelector("[data-sbc-error]");
    var submit = root.querySelector("[data-sbc-submit]");
    var result = root.querySelector("[data-sbc-result]");
    var announce = root.querySelector("[data-sbc-announce]");
    var base = root.getAttribute("data-api-base");
    var busy = false;
    if (!form || !input || !result) return;

    function showError(message) {
      error.textContent = message;
      error.hidden = false;
      input.setAttribute("aria-invalid", "true");
      input.focus();
    }

    function clearError() {
      error.textContent = "";
      error.hidden = true;
      input.removeAttribute("aria-invalid");
    }

    function setBusy(on) {
      busy = on;
      if (submit) {
        submit.disabled = on;
        submit.textContent = on ? "Checking\u2026" : "Check this number";
      }
      root.setAttribute("aria-busy", on ? "true" : "false");
    }

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      if (busy) return;
      var check = validateSerial(input.value);
      if (!check.ok) {
        showError(MESSAGES[check.error]);
        return;
      }
      clearError();
      input.value = input.value.trim();
      setBusy(true);
      if (announce) announce.textContent = "Checking " + check.serial + "\u2026";
      return lookup(base, check.serial, deps.fetch).then(function (answer) {
        setBusy(false);
        if (answer.invalid) {
          showError(answer.invalid.message || MESSAGES.invalid_characters);
          if (announce) announce.textContent = "";
          return;
        }
        var shown = renderResult(result, answer.data, { rateLimited: !!answer.rateLimited });
        if (announce) announce.textContent = shown.heading.textContent;
        shown.heading.focus();
      });
    });
  }

  function initAll() {
    var roots = document.querySelectorAll("[data-stolen-bike-check]");
    for (var i = 0; i < roots.length; i += 1) init(roots[i]);
  }

  window.StolenBikeCheck = {
    normalizeSerial: normalizeSerial,
    validateSerial: validateSerial,
    renderResult: renderResult,
    lookup: lookup,
    init: init,
    OUTCOMES: OUTCOMES,
    MESSAGES: MESSAGES,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
